import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../../helpers/request'
import { storeLocal } from '../../../helpers/stores'
import i from '../../../translations/i'
import {
    CartsSegment,
    FLASH_MS,
    LIVE_INTERVAL_MS,
    cartsFilterQuery,
    cartsSegmentParam,
    errorMessage,
    isCartsFilterEmpty,
    isCartsListFilter,
} from './carts.helpers'

export interface CartsFilters {
    activity: string | null
    search: string | null
    product_id: number | null
    sum_from: number | null
    sum_to: number | null
    registered: number | null
    has_promo_code: number | null
    delivery: string | null
    warehouse_id: number | null
    // Тип цены владельца — один из выбранных (розница, опт); без типа цены владелец считается покупателем типа по умолчанию
    price_types: number[]
    activity_from: number | null
    activity_to: number | null
}

const emptyFilters = (): CartsFilters => ({
    activity: null,
    search: null,
    product_id: null,
    sum_from: null,
    sum_to: null,
    registered: null,
    has_promo_code: null,
    delivery: null,
    warehouse_id: null,
    price_types: [],
    activity_from: null,
    activity_to: null,
})

/** Фильтры-строки; остальные — числа и в адресе приходят строками */
const TEXT_FILTERS: (keyof CartsFilters)[] = ['activity', 'search', 'delivery']

/** Live-режим общий для списка и карточки — включил в одном месте, работает в обоих */
export const LIVE_STORAGE_KEY = 'carts_live'

class CartsController {

    list: any[] = []
    stats: any = null

    /** Склады проекта для фильтра; пусто — склады выключены */
    warehouses: { id: number, name: string | null }[] = []

    /** Типы цен проекта для фильтра — под внутренним названием из настроек, как их знают сотрудники; пусто — типов нет */
    priceTypes: { id: number, label: string }[] = []

    filters: CartsFilters = emptyFilters()

    // Панель «Фильтры» под поиском по умолчанию свёрнута; раскрывается сама, если фильтры уже заданы
    filtersOpen: boolean = false

    sorting = {
        type: 'activity_at',
        action: 'desc'
    }

    pagination = {
        total: 0,
        limit: 20,
        page: 1
    }

    loading: boolean = false
    loaded: boolean = false
    updatedAt: Date | null = null

    live: boolean = storeLocal.get(LIVE_STORAGE_KEY) === true

    /** Корзины, которые изменились с прошлого обновления — их строки подсвечиваются */
    flashIds: number[] = []

    /** Подпись выбранного в фильтре товара: сам фильтр хранит только id */
    productOption: { value: number, label: string } | null = null
    productOptions: { value: number, label: string }[] = []

    private timer: ReturnType<typeof setInterval> | null = null
    private requestId: number = 0
    private knownActivity: Record<number, string | null> = {}

    constructor() {
        makeAutoObservable<CartsController, 'timer' | 'requestId' | 'knownActivity'>(this, {
            timer: false,
            requestId: false,
            knownActivity: false,
        })
    }

    /** Сколько фильтров задано в свёрнутой панели: диапазоны суммы и дат — по одному */
    get panelFiltersCount(): number {
        const f = this.filters
        const set = (value: any) => !isCartsFilterEmpty(value)

        return [
            set(f.product_id),
            set(f.sum_from) || set(f.sum_to),
            set(f.registered),
            set(f.has_promo_code),
            set(f.warehouse_id),
            set(f.price_types),
            set(f.delivery),
            set(f.activity_from) || set(f.activity_to),
        ].filter(Boolean).length
    }

    toggleFilters() {
        this.filtersOpen = !this.filtersOpen
    }

    get hasFilters(): boolean {
        return Object.values(this.filters).some((value: any) => !isCartsFilterEmpty(value))
    }

    private query(): string {
        const filter = cartsFilterQuery(this.filters)

        return `/carts?limit=${this.pagination.limit}&page=${this.pagination.page}`
            + `&sorting_type=${this.sorting.type}&sorting_action=${this.sorting.action}`
            + (filter ? `&${filter}` : '')
    }

    /** Текущие фильтры для страницы выгрузки и редактора пуша — с подписями товара, склада и типов цен */
    get segmentParam(): string {
        const warehouse = this.warehouses.find(w => w.id === this.filters.warehouse_id)
        const priceTypes = this.filters.price_types
            .map(id => this.priceTypes.find(priceType => priceType.id === id)?.label ?? `#${id}`)

        return cartsSegmentParam(this.filters, {
            product: this.productOption?.label,
            warehouse: warehouse ? (warehouse.name ?? `#${warehouse.id}`) : undefined,
            priceTypes: priceTypes.length > 0 ? priceTypes.join(', ') : undefined,
        })
    }

    get exportUrl(): string {
        return `/carts/export?carts=${this.segmentParam}`
    }

    get pushUrl(): string {
        return `/pushes/0?carts=${this.segmentParam}`
    }

    /** Список и сводка; сводка от фильтра и страницы не зависит, поэтому при них не перезапрашивается */
    refresh(silent: boolean = false) {
        this.get(silent)
        this.loadStats()
    }

    loadStats() {
        openApi('get', '/carts/stats', {}, undefined, true)
            .then(result => runInAction(() => { this.stats = result.data }))
            .catch(err => console.log(err))
    }

    /** Типы цен — один раз при открытии раздела; фильтр по ним показывается, только если они в проекте есть */
    loadPriceTypes() {
        openApi('get', '/price_types', {}, undefined, true)
            .then(result => runInAction(() => {
                this.priceTypes = (result.data ?? [])
                    .filter((el: any) => el.id)
                    .map((el: any) => ({ id: Number(el.id), label: el.private_name || el.name || `#${el.id}` }))
            }))
            .catch(err => console.log(err))
    }

    /** silent — фоновое обновление live-режима: без спиннера и без всплывающих ошибок */
    get(silent: boolean = false) {
        const requestId = ++this.requestId
        if (!silent) this.loading = true

        openApi('get', this.query(), {}, undefined, silent)
            .then(result => runInAction(() => {
                // Пока летел ответ, фильтр могли поменять — старый ответ не нужен
                if (requestId !== this.requestId) return

                if (silent) this.flash(result.data)
                this.remember(result.data)

                this.list = result.data
                this.warehouses = result.warehouses ?? []
                this.pagination.total = result.total
                this.pagination.page = Number(result.page)
                this.pagination.limit = Number(result.limit)
                this.updatedAt = new Date()
                this.loaded = true
            }))
            .catch(err => {
                if (silent) console.log(err)
                else notification.error({ message: i.t('carts:errors.common'), description: errorMessage(err) })
            })
            .finally(() => runInAction(() => {
                if (requestId === this.requestId) this.loading = false
            }))
    }

    private remember(list: any[]) {
        this.knownActivity = {}
        list.forEach((cart: any) => { this.knownActivity[cart.id] = cart.activityAt })
    }

    private flash(list: any[]) {
        const changed = list
            .filter((cart: any) => !(cart.id in this.knownActivity) || this.knownActivity[cart.id] !== cart.activityAt)
            .map((cart: any) => cart.id)

        if (changed.length === 0) return

        this.flashIds = changed
        setTimeout(() => runInAction(() => {
            this.flashIds = this.flashIds.filter((id: number) => !changed.includes(id))
        }), FLASH_MS)
    }

    setFilter(key: keyof CartsFilters, value: any) {
        // @ts-ignore — значения фильтров разных типов, ключ проверен типом выше
        this.filters[key] = value === undefined || value === '' ? null : value
        this.pagination.page = 1
        this.get()
    }

    /** Несколько фильтров одним запросом — иначе диапазон дат ушёл бы двумя */
    setFilters(values: Partial<CartsFilters>) {
        Object.keys(values).forEach((key: string) => {
            const value = values[key as keyof CartsFilters]
            // @ts-ignore
            this.filters[key] = value === undefined || value === '' ? null : value
        })
        this.pagination.page = 1
        this.get()
    }

    resetFilters() {
        this.filters = emptyFilters()
        this.productOption = null
        this.pagination.page = 1
        this.get()
    }

    /** Вернуться в список с условиями со страницы выгрузки или из редактора пуша */
    applySegment({ filters, labels }: CartsSegment) {
        const next = emptyFilters()

        Object.keys(next).forEach((key: string) => {
            const value = filters[key]
            if (isCartsFilterEmpty(value)) return

            // @ts-ignore — тип значения задают TEXT_FILTERS и фильтры-списки
            next[key] = isCartsListFilter(key)
                ? (Array.isArray(value) ? value : [value]).map(Number).filter((id: number) => id > 0)
                : TEXT_FILTERS.includes(key as keyof CartsFilters) ? `${value}` : Number(value)
        })

        this.filters = next
        this.productOption = next.product_id ? { value: next.product_id, label: labels.product ?? `#${next.product_id}` } : null
        this.filtersOpen = this.panelFiltersCount > 0
        this.pagination.page = 1
    }

    filterByProduct(productId: number | null, label: string = '') {
        this.productOption = productId ? { value: productId, label } : null
        this.setFilter('product_id', productId)
    }

    searchProducts(search: string) {
        const query = search.trim()
        if (query === '') {
            this.productOptions = []
            return
        }

        const filter = /^\d+$/.test(query) ? `filter[product_id]=${query}` : `filter[name]=${encodeURIComponent(query)}`

        openApi('get', `/products/select?limit=20&page=1&${filter}`, {}, undefined, true)
            .then(result => runInAction(() => {
                this.productOptions = (result.data ?? []).map((product: any) => ({ value: product.id, label: product.name }))
            }))
            .catch(err => console.log(err))
    }

    setSort(type: string, action: string) {
        this.sorting.type = type
        this.sorting.action = action
        this.get()
    }

    setPage(page: number) {
        this.pagination.page = page
        this.get()
    }

    setLive(value: boolean) {
        this.live = value
        storeLocal.set(LIVE_STORAGE_KEY, value)

        if (value) {
            this.refresh(true)
            this.startLive()
        } else {
            this.stopLive()
        }
    }

    startLive() {
        this.stopLive()
        if (!this.live) return

        // Свёрнутая вкладка не опрашивает сервер — догонит при возвращении
        this.timer = setInterval(() => {
            if (!document.hidden) this.refresh(true)
        }, LIVE_INTERVAL_MS)
    }

    stopLive() {
        if (this.timer) clearInterval(this.timer)
        this.timer = null
    }
}

export default new CartsController()
